import React, { useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Cada tipo pede campos diferentes e explica o que a URL significa nele.
const TIPOS = {
	typebot: {
		rotulo: "Typebot",
		explicacao:
			"O endereço do seu Typebot (viewer) e o identificador público do fluxo.",
		rotuloUrl: "Endereço do Typebot",
		exemploUrl: "https://bot.suaempresa.com.br",
	},
	n8n: {
		rotulo: "n8n",
		explicacao:
			"A URL do nó Webhook do seu fluxo no n8n. O sistema manda o que o cliente escreveu e envia de volta o que o fluxo responder.",
		rotuloUrl: "URL do webhook no n8n",
		exemploUrl: "https://n8n.suaempresa.com.br/webhook/atendimento",
	},
	webhook: {
		rotulo: "Webhook próprio",
		explicacao:
			"Seu próprio serviço. Recebe um POST com o contato e a mensagem, e responde com um JSON contendo reply, message ou text.",
		rotuloUrl: "URL do seu serviço",
		exemploUrl: "https://api.suaempresa.com.br/atendimento",
	},
};

const VAZIO = {
	name: "",
	type: "typebot",
	isActive: true,
	url: "",
	typebotSlug: "",
	keywordFinish: "",
	keywordRestart: "",
	unknownMessage: "",
	expiresMinutes: 0,
	authHeader: "",
};

const QueueIntegrationModal = ({ open, onClose, integrationId, onSaved }) => {
	const [dados, setDados] = useState(VAZIO);
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!open) return;

		if (!integrationId) {
			setDados(VAZIO);
			return;
		}

		(async () => {
			try {
				const { data } = await api.get(`/queue-integrations/${integrationId}`);
				setDados({
					name: data.name || "",
					type: data.type || "typebot",
					isActive: data.isActive !== false,
					url: data.url || "",
					typebotSlug: data.typebotSlug || "",
					keywordFinish: data.keywordFinish || "",
					keywordRestart: data.keywordRestart || "",
					unknownMessage: data.unknownMessage || "",
					expiresMinutes: data.expiresMinutes || 0,
					authHeader: data.authHeader || "",
				});
			} catch (err) {
				toastError(err);
			}
		})();
	}, [open, integrationId]);

	const alterar = (campo, valor) =>
		setDados((atual) => ({ ...atual, [campo]: valor }));

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const corpo = { ...dados, expiresMinutes: Number(dados.expiresMinutes) || 0 };
			const { data } = integrationId
				? await api.put(`/queue-integrations/${integrationId}`, corpo)
				: await api.post("/queue-integrations", corpo);

			toast.success(
				integrationId ? "Integração alterada." : "Integração criada."
			);
			onSaved?.(data);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const tipo = TIPOS[dados.type] || TIPOS.typebot;

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<form onSubmit={salvar}>
					<DialogHeader>
						<DialogTitle>
							{integrationId ? "Editar integração" : "Nova integração"}
						</DialogTitle>
						<DialogDescription>
							Um bot de fora que atende no seu lugar. Depois de salvar, escolha
							esta integração numa fila para ela começar a responder.
						</DialogDescription>
					</DialogHeader>

					<div className="grid gap-3 py-4">
						<div>
							<Label htmlFor="name">Nome</Label>
							<Input
								id="name"
								required
								placeholder="Ex.: Triagem do suporte"
								value={dados.name}
								onChange={(e) => alterar("name", e.target.value)}
							/>
						</div>

						<div>
							<Label htmlFor="type">Tipo</Label>
							<Select
								value={dados.type}
								onValueChange={(v) => alterar("type", v)}
							>
								<SelectTrigger id="type">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Object.entries(TIPOS).map(([chave, t]) => (
										<SelectItem key={chave} value={chave}>
											{t.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs text-muted-foreground">
								{tipo.explicacao}
							</p>
						</div>

						<div>
							<Label htmlFor="url">{tipo.rotuloUrl}</Label>
							<Input
								id="url"
								required
								placeholder={tipo.exemploUrl}
								value={dados.url}
								onChange={(e) => alterar("url", e.target.value)}
							/>
						</div>

						{dados.type === "typebot" && (
							<div>
								<Label htmlFor="typebotSlug">Identificador do fluxo</Label>
								<Input
									id="typebotSlug"
									required
									placeholder="meu-fluxo"
									value={dados.typebotSlug}
									onChange={(e) => alterar("typebotSlug", e.target.value)}
								/>
								<p className="mt-1 text-xs text-muted-foreground">
									É o que aparece no fim do link público do fluxo.
								</p>
							</div>
						)}

						{dados.type !== "typebot" && (
							<div>
								<Label htmlFor="authHeader">
									Cabeçalho de autenticação{" "}
									<span className="font-normal text-muted-foreground">
										(opcional)
									</span>
								</Label>
								<Input
									id="authHeader"
									placeholder="Authorization: Bearer seu-token"
									value={dados.authHeader}
									onChange={(e) => alterar("authHeader", e.target.value)}
								/>
							</div>
						)}

						<div className="grid gap-3 sm:grid-cols-2">
							<div>
								<Label htmlFor="keywordFinish">Palavra para sair do bot</Label>
								<Input
									id="keywordFinish"
									placeholder="atendente"
									value={dados.keywordFinish}
									onChange={(e) => alterar("keywordFinish", e.target.value)}
								/>
							</div>
							<div>
								<Label htmlFor="keywordRestart">Palavra para recomeçar</Label>
								<Input
									id="keywordRestart"
									placeholder="reiniciar"
									value={dados.keywordRestart}
									onChange={(e) => alterar("keywordRestart", e.target.value)}
								/>
							</div>
						</div>
						<p className="-mt-1 text-xs text-muted-foreground">
							Sem a palavra de saída, quem entrar no bot só sai quando alguém da
							equipe assumir a conversa na mão.
						</p>

						<div>
							<Label htmlFor="expiresMinutes">
								Encerrar a conversa com o bot após
							</Label>
							<div className="flex items-center gap-2">
								<Input
									id="expiresMinutes"
									type="number"
									min="0"
									className="w-28"
									value={dados.expiresMinutes}
									onChange={(e) => alterar("expiresMinutes", e.target.value)}
								/>
								<span className="text-sm text-muted-foreground">
									minutos parada (0 = nunca)
								</span>
							</div>
						</div>

						<div>
							<Label htmlFor="unknownMessage">
								O que dizer quando o bot não responder
							</Label>
							<Textarea
								id="unknownMessage"
								rows={2}
								placeholder="Não entendi. Pode repetir?"
								value={dados.unknownMessage}
								onChange={(e) => alterar("unknownMessage", e.target.value)}
							/>
						</div>

						<div className="flex items-center gap-3 border-t pt-3">
							<Switch
								id="isActive"
								checked={dados.isActive}
								onCheckedChange={(v) => alterar("isActive", v)}
							/>
							<Label htmlFor="isActive" className="cursor-pointer">
								Ativa
								<span className="ml-1 font-normal text-muted-foreground">
									— desligada, as filas dela voltam a atender pelo menu.
								</span>
							</Label>
						</div>
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={onClose}>
							Cancelar
						</Button>
						<Button type="submit" disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default QueueIntegrationModal;
